import React from "react";
import DOMPurify from "dompurify";
import { useAppLoadingContext } from "../../Context/AppLoadingContext";
import { AppLoadingContextTypes } from "../../Context/ContextTypes/AppLoadingContextTypes";
import { Box, Grid, Link, Typography } from "@mui/material";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { colors } from "../../styles/themes/ctfs-theme";

type ProductCardPropTypes = {
  cardOneTitle: string;
  cardOneDescription: string;
  cardOneFooterNote: string;
  cardOneFooterLink: string;
  cardOneFooterAriaLabel: string;
  cardTwoTitle: string;
  cardTwoDescription: string;
  cardTwoFooterNote: string;
  cardTwoFooterLink: string;
  cardTwoFooterAriaLabel: string;
  cardOneItems: Array<{
    itemLinkText: string;
    itemLinkUrl: string;
    itemLinkAriaLabel: string;
  }>;
  cardTwoItems: Array<{
    itemLinkText: string;
    itemLinkUrl: string;
    itemLinkAriaLabel: string;
  }>;
  isInEditor: boolean;
  cqPath: string;
  cqType: string;
};

export default function ProductCardReact(props: ProductCardPropTypes) {
  // translation
  const { translationsData } = useAppLoadingContext() as AppLoadingContextTypes;
  const DomPurify = (tag: string) => {
    return DOMPurify.sanitize(translationsData[tag]);
  };

  const translatedData = {
    // title and button
    cardOneTitle: DomPurify(props.cardOneTitle),
    cardOneDescription: DomPurify(props.cardOneDescription),
    cardOneFooterNote: DomPurify(props.cardOneFooterNote),
    cardOneFooterLink: DomPurify(props.cardOneFooterLink),
    cardOneFooterAriaLabel: DomPurify(props.cardOneFooterAriaLabel),

    cardTwoTitle: DomPurify(props.cardTwoTitle),
    cardTwoDescription: DomPurify(props.cardTwoDescription),
    cardTwoFooterNote: DomPurify(props.cardTwoFooterNote),
    cardTwoFooterLink: DomPurify(props.cardTwoFooterLink),
    cardTwoFooterAriaLabel: DomPurify(props.cardTwoFooterAriaLabel),
  };

  return (
    <Box
      component="section"
      id="productCardSection"
      className="content-section"
      sx={{
        marginTop: "0px",
        backgroundColor: "rgba(18, 18, 18)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: {lg:"0px 20px"}
      }}
    >
      <Grid
        container
        display="flex"
        flexDirection={{ xs: "column", lg: "row" }}
        alignItems={{ xs: "center", lg: "flex-start" }}
        justifyContent="center"
        padding={{ xs: "0px", lg: "80px 0px" }}
        maxWidth={{ xs: "100%", lg: "1296px" }}
      >
        {/* card 1 */}
        <Grid item xs={12} lg={6} display="flex" justifyContent={{ xs: "center", lg: "center" }}>
          <Box
            sx={{
              borderRadius: "30px",
              backgroundColor: "#262626",
              margin: {
                xs: "5% 12px 0% 12px",
                lg: "0% 10px 0% 0px",
              },
              padding: { xs: "36px 32px", lg: "72px" },
              width: { xs: "100%", sm: "458px", md: "458px", lg: "638px" },
            }}
          >
            <Typography
              variant="h3"
              color="#fff"
              textAlign={{ xs: "center", lg: "left" }}
              sx={{
                marginTop: `${translatedData.cardOneDescription ? `0px` : `20px`}`,
                marginBottom: `${translatedData.cardOneDescription ? `0px` : `40px`}`,
                fontSize: { xs: "28px", lg: "36px" },
                color: `${colors.NeutralWhite} !important`,
              }}
            >
              {translatedData.cardOneTitle}
            </Typography>

            {translatedData.cardOneDescription && (
              <Typography
                variant="body1"
                color="#fff"
                textAlign={{ xs: "center", lg: "left" }}
                sx={{
                  marginTop: "20px",
                  marginBottom: { xs: "40px", lg: "60px" },
                  paddingRight: "0px",
                  fontSize: { xs: "16px", lg: "18px" },
                }}
              >
                {translatedData.cardOneDescription}
              </Typography>
            )}

            <Grid container display="flex" flexDirection="column" marginBottom="40px">
              {props.cardOneItems?.map((item, index) => (
                <Grid
                  item
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                  color="#fff"
                  borderTop={`${index === 0 ? `1px solid white` : `none`}`}
                  borderBottom="1px solid white"
                >
                  <Typography
                    variant="body1"
                    fontWeight="700"
                    sx={{
                      // borderBottom: "1px solid white",
                      padding: "20px 0px",
                      textDecoration: "none rgba(197, 0, 3, 0.0)",
                      fontWeight: "normal",
                      fontSize: { xs: "14px", lg: "18px" },
                    }}
                  >
                    <Link
                      href={DomPurify(item.itemLinkUrl)}
                      aria-label={DomPurify(item.itemLinkAriaLabel)}
                      color="#fff"
                      sx={{
                        textDecoration: "none rgba(197, 0, 3, 0.0)",
                        fontWeight: "normal",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {DomPurify(item.itemLinkText)}
                    </Link>
                  </Typography>
                  <KeyboardArrowRight
                    aria-hidden={true}
                    sx={{ height: "1.8rem", width: "1.8rem", color: "white" }}
                  />
                </Grid>
              ))}
            </Grid>

            <Typography
              variant="body1"
              fontWeight="700"
              textAlign={{ xs: "center", lg: "left" }}
              sx={{
                display: "flex",
                justifyContent: { xs: "center", lg: "left" },
                padding: "0px",
                textDecoration: "none rgba(197, 0, 3, 0.0) !important",
                color: "#fff",
                fontWeight: "normal",
                fontStyle: "italic",
                fontSize: "12px",
              }}
              aria-label={translatedData.cardOneFooterAriaLabel}
            >
              {translatedData.cardOneFooterNote}
            </Typography>
          </Box>
        </Grid>

        {/* card 2 */}
        <Grid
          item
          xs={12}
          lg={6}
          width="100%"
          display="flex"
          justifyContent={{ xs: "center", lg: "left" }}
        >
          <Box
            sx={{
              borderRadius: "30px",
              backgroundColor: "#262626",
              margin: {
                xs: "5% 12px 0% 12px",
                lg: "40% 0px 0% 10px",
              },
              padding: { xs: "36px 32px", lg: "72px" },
              width: { xs: "100%", sm: "458px", md: "458px", lg: "638px" },
            }}
          >
            <Typography
              variant="h3"
              textAlign={{ xs: "center", lg: "left" }}
              sx={{
                fontSize: { xs: "28px", lg: "36px" },
                marginTop: `${translatedData.cardTwoDescription ? `0px` : `20px`}`,
                marginBottom: `${translatedData.cardTwoDescription ? `0px` : `40px`}`,
                color: `${colors.NeutralWhite} !important`,
              }}
            >
              {translatedData.cardTwoTitle}
            </Typography>

            {translatedData.cardTwoDescription && (
              <Typography
                variant="body1"
                color="#fff"
                textAlign={{ xs: "center", lg: "left" }}
                sx={{
                  marginTop: "20px",
                  marginBottom: "40px",
                }}
              >
                {translatedData.cardTwoDescription}
              </Typography>
            )}

            <Grid container display="flex" flexDirection="column" marginBottom="40px">
              {props.cardTwoItems?.map((item, index) => (
                <Grid
                  item
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  color="#fff"
                  borderTop={`${index === 0 ? `1px solid white` : `none`}`}
                  borderBottom="1px solid white"
                >
                  <Typography
                    variant="body1"
                    fontWeight="700"
                    sx={{
                      padding: "20px 0px",
                      textDecoration: "none rgba(197, 0, 3, 0.0)",
                      fontWeight: "normal",
                      fontSize: { xs: "14px", lg: "18px" },
                    }}
                  >
                    <Link
                      href={DomPurify(item.itemLinkUrl)}
                      aria-label={DomPurify(item.itemLinkAriaLabel)}
                      color="#fff"
                      sx={{
                        textDecoration: "none rgba(197, 0, 3, 0.0)",
                        fontWeight: "normal",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {DomPurify(item.itemLinkText)}
                    </Link>
                  </Typography>
                  <KeyboardArrowRight
                    aria-hidden={true}
                    sx={{ height: "1.8rem", width: "1.8rem", color: "white" }}
                  />
                </Grid>
              ))}
            </Grid>

            {translatedData.cardTwoFooterLink ? (
              <Typography
                variant="body1"
                fontWeight="700"
                display="flex"
                alignItems="center"
                textAlign={{ xs: "center", lg: "left" }}
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", lg: "left" },
                  padding: { xs: "0px", sm: "0px", md: "0px", lg: "0px" },
                  textDecoration: "underline white !important",
                  fontSize: "12px",
                }}
              >
                <Link
                  href={translatedData.cardTwoFooterLink}
                  aria-label={translatedData.cardTwoFooterAriaLabel}
                  color="#fff"
                  sx={{
                    fontSize: { xs: "14px", lg: "18px" },
                    fontWeight: "normal",
                    textDecoration: "underline white !important",
                  }}
                >
                  {translatedData.cardTwoFooterNote}
                </Link>
                <KeyboardArrowRight
                  aria-hidden={true}
                  sx={{ height: "1.2rem", width: "1.2rem", color: "white" }}
                />
              </Typography>
            ) : (
              <Typography
                variant="body1"
                fontWeight="700"
                textAlign={{ xs: "center", sm: "center", md: "center", lg: "left" }}
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", sm: "center", md: "center", lg: "left" },
                  padding: { xs: "0px", sm: "0px", md: "0px", lg: "0px" },
                  textDecoration: "none rgba(197, 0, 3, 0.0) !important",
                  color: "#fff",
                  fontSize: "12px",
                  fontStyle: "italic",
                }}
                aria-label={translatedData.cardTwoFooterAriaLabel}
              >
                {translatedData.cardTwoFooterNote}
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
      {/* triangle color borders */}
      <Grid container xs={12} sm={12} md={12} lg={12} paddingTop="20px">
        <Box
          component="div"
          className="trianleBgBorder"
          sx={{ height: { xs: "16px", lg: "32px" } }}
        />
      </Grid>
    </Box>
  );
}
